import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

/**
 * Modal for assigning a cluster to a Space record.
 */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    ds.input.select.singleEc.Component,
    ds.notice.Component,
  ],
  selector: 'atn-space-cluster-assignment-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /** @see {@link state.space.full.clusters.Collection} */
  public readonly clustersEc = ngCore.inject(
    state.space.full.clusters.Collection,
  )

  /** @see {@link state.space.full.records.plugins.clusterAssignment.Plugin} */
  public readonly clusterAssignmentPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.clusterAssignment.Plugin)
    .get()

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** Selection target for the cluster selector. */
  public readonly selectionTarget =
    state.space.full.records.SPACE_RECORDS_SELECTION_TARGET

  /** Group used to scope cluster options for this modal. */
  public readonly clusterAssignmentOptionsGroupId =
    state.space.full.records.plugins.clusterAssignment
      .CLUSTER_ASSIGNMENT_OPTIONS_GROUP_ID

  /**
   * Callers must choose whether "None" is valid for their workflow.
   * Record details can unassign a single record; records list assignment cannot.
   */
  public readonly isClusterRequiredSg = ngCore.input<boolean>(false, {
    alias: 'isClusterRequired',
  })

  /** Modal title for the active assignment workflow. */
  public readonly titleSg = ngCore.computed(
    (
      recordCount = this.clusterAssignmentPlugin.selectors.assignmentRecords.asSignal()
        .length,
    ) =>
      `Cluster Assignment${
        recordCount > 1 ? ` (${recordCount} Space Records selected)` : ''
      }`,
  )

  /** Whether user can save the selected cluster assignment. */
  public readonly canSaveSg = ngCore.computed(
    (
      assignmentRecords = this.clusterAssignmentPlugin.selectors.assignmentRecords.asSignal(),
      selectedClusterId = this.clusterAssignmentPlugin.selectors.selectedClusterId.asSignal(),
      isSubmitting = this.clusterAssignmentPlugin.selectors.isSubmitting.asSignal(),
      isClusterRequired = this.isClusterRequiredSg(),
    ) => {
      if (assignmentRecords.length === 0 || isSubmitting) {
        return false
      }

      // The selector uses undefined for "None". Required mode belongs to the
      // records-list assign workflow, where submitting "None" is not valid.
      if (TypeChecker.isUndefined(selectedClusterId)) {
        if (isClusterRequired) {
          return false
        }

        // Record details can unassign one currently-assigned record.
        const [record] = assignmentRecords
        return (
          assignmentRecords.length === 1 &&
          TypeChecker.isNotNullish(record?.clusterId)
        )
      }

      return (
        ents.extra.space.Cluster.Id.isId(selectedClusterId) &&
        assignmentRecords.some(record => record.clusterId !== selectedClusterId)
      )
    },
  )

  /** Save assignment. */
  public saveClickHandler(): void {
    this.clusterAssignmentPlugin.actions.submit
      .from('Space.Records.ClusterAssignment.Modal.Save.Clicked')
      .dispatch()
  }

  /** Cancel assignment. */
  public cancelClickHandler(): void {
    this.clusterAssignmentPlugin.actions.cancel
      .from('Space.Records.ClusterAssignment.Modal.Cancel.Clicked')
      .dispatch()
  }
}
